import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { productService } from "../services/productService";
import { useCart } from "../context/CartContext";
import Button from "../components/Button/Button";
import Input from "../components/Input/Input";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import { FaCartPlus, FaCheck, FaSpinner } from "react-icons/fa";

// Animation keyframes
const slideUp = keyframes`
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100px);
    opacity: 0;
  }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -30px, 0);
  }
  70% {
    transform: translate3d(0, -15px, 0);
  }
  90% {
    transform: translate3d(0, -4px, 0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ShopWrapper = styled.div`
  min-height: 100vh;
  background-color: ${(props) => props.theme.colors.background.secondary};
  padding-top: 40px; /* Account for fixed header */
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
`;

const ShopHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: ${(props) => props.theme.spacing.xxl};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  margin-bottom: ${(props) => props.theme.spacing.xl};
  text-align: center;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 70%
    );
    animation: ${fadeIn} 2s ease-in-out;
  }
`;

const ShopTitle = styled.h1`
  font-size: ${(props) => props.theme.fontSize.xxxl};
  font-weight: ${(props) => props.theme.fontWeight.bold};
  color: white;
  margin-bottom: ${(props) => props.theme.spacing.md};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
`;

const ShopSubtitle = styled.p`
  font-size: ${(props) => props.theme.fontSize.lg};
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`;

// Animated View Cart Notification
const ViewCartNotification = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 300px;
  animation: ${(props) => (props.show ? slideUp : slideDown)} 0.3s ease-in-out;
  transform: ${(props) => (props.show ? "translateY(0)" : "translateY(100px)")};
  opacity: ${(props) => (props.show ? "1" : "0")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    left: 20px;
    right: 20px;
    min-width: auto;
  }
`;

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  animation: ${bounce} 1s ease;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const NotificationMessage = styled.div`
  font-size: 0.8rem;
  opacity: 0.9;
`;

const ViewCartButton = styled(Link)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

const CloseNotificationButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 0.25rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

// Enhanced Product Card and Actions
const ProductCard = styled.div`
  background-color: ${(props) => props.theme.colors.background.primary};
  border: 1px solid ${(props) => props.theme.colors.border.dark};
  border-radius: ${(props) => props.theme.borderRadius.xxl};
  overflow: hidden;
  transition: all 0.3s ease;
  display: ${(props) => (props.viewMode === "list" ? "flex" : "block")};
  position: relative;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    border-color: ${(props) => props.theme.colors.primary}40;
  }
`;

const ProductActions = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: auto;
`;

const AddToCartButton = styled(Button)`
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3);
  }

  &.adding {
    animation: ${pulse} 0.6s ease;
  }

  &::after {
    content: "✓";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    color: white;
    font-size: 1.2rem;
    font-weight: bold;
    transition: transform 0.3s ease;
  }

  &.added::after {
    transform: translate(-50%, -50%) scale(1);
  }

  &.added {
    background-color: #10b981 !important;
    border-color: #10b981 !important;
  }

  .button-text {
    transition: opacity 0.3s ease;
  }

  &.added .button-text {
    opacity: 0;
  }
`;

// Reusing existing styled components from the original code
const ShopContent = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${(props) => props.theme.spacing.xxl};

  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const FilterSidebar = styled.div`
  background-color: ${(props) => props.theme.colors.background.primary};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.sm};
  height: fit-content;
  position: sticky;
  top: ${(props) => props.theme.spacing.xl};

  @media (max-width: ${(props) => props.theme.breakpoints.desktop}) {
    position: static;
    margin-bottom: ${(props) => props.theme.spacing.xl};
  }
`;

const FilterSection = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.xl};
  padding-bottom: ${(props) => props.theme.spacing.lg};
  border-bottom: 1px solid ${(props) => props.theme.colors.border.light};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const FilterTitle = styled.h3`
  font-size: ${(props) => props.theme.fontSize.base};
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  color: ${(props) => props.theme.colors.text.primary};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  padding: ${(props) => props.theme.spacing.sm} 0;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.sm};
  color: ${(props) => props.theme.colors.text.primary};
  transition: color ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }

  input[type="checkbox"],
  input[type="radio"] {
    margin: 0;
    accent-color: ${(props) => props.theme.colors.primary};
  }
`;

const PriceRangeContainer = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.sm};
  align-items: center;
`;

const PriceInput = styled.input`
  width: 80px;
  padding: ${(props) => props.theme.spacing.sm};
  border: 1px solid ${(props) => props.theme.colors.border.light};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  font-size: ${(props) => props.theme.fontSize.xs};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const ClearFiltersButton = styled.button`
  width: 100%;
  padding: ${(props) => props.theme.spacing.sm};
  background: none;
  border: 1px solid ${(props) => props.theme.colors.border.medium};
  border-radius: ${(props) => props.theme.borderRadius.md};
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: ${(props) => props.theme.fontSize.sm};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    border-color: ${(props) => props.theme.colors.error};
    color: ${(props) => props.theme.colors.error};
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing.xl};
  padding: ${(props) => props.theme.spacing.lg};
  background-color: ${(props) => props.theme.colors.background.primary};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  box-shadow: ${(props) => props.theme.shadows.sm};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${(props) => props.theme.spacing.md};
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.md};
  flex: 1;
  max-width: 400px;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    max-width: none;
  }
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
`;

const SortLabel = styled.span`
  font-size: ${(props) => props.theme.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
  font-weight: ${(props) => props.theme.fontWeight.medium};
  white-space: nowrap;
`;

const SortSelect = styled.select`
  padding: ${(props) => props.theme.spacing.sm}
    ${(props) => props.theme.spacing.md};
  border: 1px solid ${(props) => props.theme.colors.border.light};
  border-radius: ${(props) => props.theme.borderRadius.md};
  font-size: ${(props) => props.theme.fontSize.sm};
  color: ${(props) => props.theme.colors.text.primary};
  background-color: ${(props) => props.theme.colors.background.primary};
  cursor: pointer;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primaryLight};
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
  padding: 0 ${(props) => props.theme.spacing.sm};
`;

const ResultsCount = styled.span`
  font-size: ${(props) => props.theme.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid ${(props) => props.theme.colors.border.light};
  border-radius: ${(props) => props.theme.borderRadius.md};
  overflow: hidden;
`;

const ViewButton = styled.button`
  padding: ${(props) => props.theme.spacing.sm}
    ${(props) => props.theme.spacing.md};
  border: none;
  background-color: ${(props) =>
    props.active ? props.theme.colors.primary : "transparent"};
  color: ${(props) =>
    props.active
      ? props.theme.colors.text.inverse
      : props.theme.colors.text.secondary};
  font-size: ${(props) => props.theme.fontSize.sm};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    background-color: ${(props) =>
      props.active
        ? props.theme.colors.primaryHover
        : props.theme.colors.background.secondary};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.viewMode === "grid"
      ? "repeat(auto-fill, minmax(300px, 1fr))"
      : "1fr"};
  gap: ${(props) => props.theme.spacing.xl};
  margin-bottom: ${(props) => props.theme.spacing.xxl};
`;

const ProductImage = styled.div`
  width: ${(props) => (props.viewMode === "list" ? "200px" : "100%")};
  height: ${(props) => (props.viewMode === "list" ? "150px" : "220px")};
  background-color: ${(props) => props.theme.colors.background.secondary};
  background-image: url(${(props) => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) =>
      !props.image
        ? "linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)"
        : "none"};
    background-size: ${(props) => (!props.image ? "20px 20px" : "auto")};
    background-position: ${(props) =>
      !props.image ? "0 0, 0 10px, 10px -10px, -10px 0px" : "auto"};
  }
`;

const ProductBadge = styled.div`
  position: absolute;
  top: ${(props) => props.theme.spacing.md};
  left: ${(props) => props.theme.spacing.md};
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.text.inverse};
  padding: ${(props) => props.theme.spacing.xs}
    ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  font-size: ${(props) => props.theme.fontSize.xs};
  font-weight: ${(props) => props.theme.fontWeight.medium};
`;

const ProductContent = styled.div`
  padding: ${(props) => props.theme.spacing.lg};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProductHeader = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ProductCategory = styled.span`
  font-size: ${(props) => props.theme.fontSize.xs};
  color: ${(props) => props.theme.colors.primary};
  font-weight: ${(props) => props.theme.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProductTitle = styled.h3`
  font-size: ${(props) => props.theme.fontSize.lg};
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  color: ${(props) => props.theme.colors.text.primary};
  margin: ${(props) => props.theme.spacing.xs} 0
    ${(props) => props.theme.spacing.sm};
  line-height: 1.3;
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.xs};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
`;

const Star = styled.span`
  color: ${(props) => (props.filled ? "#ffd700" : "#e0e0e0")};
  font-size: ${(props) => props.theme.fontSize.sm};
`;

const RatingCount = styled.span`
  font-size: ${(props) => props.theme.fontSize.xs};
  color: ${(props) => props.theme.colors.text.secondary};
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const CurrentPrice = styled.span`
  font-size: ${(props) => props.theme.fontSize.xl};
  font-weight: ${(props) => props.theme.fontWeight.bold};
  color: ${(props) => props.theme.colors.primary};
`;

const OriginalPrice = styled.span`
  font-size: ${(props) => props.theme.fontSize.base};
  color: ${(props) => props.theme.colors.text.secondary};
  text-decoration: line-through;
`;

const ProductDescription = styled.p`
  font-size: ${(props) => props.theme.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.viewMode === "list" ? 3 : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [viewMode, setViewMode] = useState("grid");
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [inStockOnly, setInStockOnly] = useState(false);
  const [onSaleOnly, setOnSaleOnly] = useState(false);

  // Cart notification states
  const [showNotification, setShowNotification] = useState(false);
  const [addedProduct, setAddedProduct] = useState(null);
  const [addingStates, setAddingStates] = useState({});

  const { addItem } = useCart();

  // All the existing useEffect hooks and functions remain the same...
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await productService.getProductCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const page = parseInt(searchParams.get("page")) || 1;
    const search = searchParams.get("search") || "";
    const sort = searchParams.get("sort") || "date";
    const categories =
      searchParams.get("categories")?.split(",").filter(Boolean) || [];
    const minPrice = searchParams.get("min_price") || "";
    const maxPrice = searchParams.get("max_price") || "";
    const inStock = searchParams.get("in_stock") === "true";
    const onSale = searchParams.get("on_sale") === "true";

    setCurrentPage(page);
    setSearchQuery(search);
    setSortBy(sort);
    setSelectedCategories(categories);
    setPriceRange({ min: minPrice, max: maxPrice });
    setInStockOnly(inStock);
    setOnSaleOnly(onSale);
  }, [searchParams]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        let params = {
          page: currentPage,
          per_page: 12,
          orderby: sortBy,
          order: sortBy === "price" ? "asc" : "desc",
        };

        if (selectedCategories.length > 0) {
          params.category = selectedCategories.join(",");
        }

        if (priceRange.min) {
          params.min_price = priceRange.min;
        }

        if (priceRange.max) {
          params.max_price = priceRange.max;
        }

        if (inStockOnly) {
          params.stock_status = "instock";
        }

        if (onSaleOnly) {
          params.on_sale = true;
        }

        let response;

        if (searchQuery.trim()) {
          response = await productService.searchProducts(searchQuery, params);
        } else {
          response = await productService.getProducts(params);
        }

        setProducts(response.products);
        setTotalPages(response.totalPages);
        setTotalProducts(response.total);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [
    currentPage,
    searchQuery,
    sortBy,
    selectedCategories,
    priceRange,
    inStockOnly,
    onSaleOnly,
  ]);

  const updateUrlParams = (newParams) => {
    const params = new URLSearchParams(searchParams);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value && value !== "") {
        if (Array.isArray(value)) {
          params.set(key, value.join(","));
        } else {
          params.set(key, value.toString());
        }
      } else {
        params.delete(key);
      }
    });

    params.set("page", "1");
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateUrlParams({ search: searchQuery });
  };

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSortBy(newSort);
    updateUrlParams({ sort: newSort });
  };

  const handleCategoryChange = (categoryId) => {
    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];

    setSelectedCategories(newCategories);
    updateUrlParams({ categories: newCategories });
  };

  const handlePriceRangeChange = () => {
    updateUrlParams({
      min_price: priceRange.min,
      max_price: priceRange.max,
    });
  };

  const handleFilterChange = (filterType, value) => {
    const updates = {};

    switch (filterType) {
      case "inStock":
        setInStockOnly(value);
        updates.in_stock = value;
        break;
      case "onSale":
        setOnSaleOnly(value);
        updates.on_sale = value;
        break;
    }

    updateUrlParams(updates);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setPriceRange({ min: "", max: "" });
    setInStockOnly(false);
    setOnSaleOnly(false);
    setSearchQuery("");
    setSearchParams({});
  };

  // Enhanced Add to Cart function with animation
  const handleAddToCart = (product) => {
    // Set adding state
    setAddingStates((prev) => ({ ...prev, [product.id]: "adding" }));

    // Add to cart
    addItem(product, 1);

    // Show added state
    setTimeout(() => {
      setAddingStates((prev) => ({ ...prev, [product.id]: "added" }));
      setAddedProduct(product);
      setShowNotification(true);

      // Reset button state after animation
      setTimeout(() => {
        setAddingStates((prev) => ({ ...prev, [product.id]: null }));
      }, 1000);
    }, 300);
  };

  const closeNotification = () => {
    setShowNotification(false);
  };

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const renderStars = (rating) => {
    return (
      <Stars>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} filled={star <= rating}>
            ★
          </Star>
        ))}
      </Stars>
    );
  };

  return (
    <ShopWrapper>
      <Container>
        {/* <ShopHeader>
          <ShopTitle>Elevate Your Digital Journey</ShopTitle>
          <ShopSubtitle>
            Explore a curated collection of cutting-edge templates, tools, and
            resources designed to boost creativity and accelerate success.
          </ShopSubtitle>
        </ShopHeader> */}

        <ShopContent>
          <FilterSidebar>
            <FilterSection>
              <SearchContainer>
                <form onSubmit={handleSearch} style={{ width: "100%" }}>
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </form>
              </SearchContainer>
            </FilterSection>

            {categories.length > 0 && (
              <FilterSection>
                <FilterTitle>Categories</FilterTitle>
                {categories.map((category) => (
                  <FilterOption key={category.id}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(
                        category.id.toString()
                      )}
                      onChange={() =>
                        handleCategoryChange(category.id.toString())
                      }
                    />
                    <span>
                      {category.name} ({category.count})
                    </span>
                  </FilterOption>
                ))}
              </FilterSection>
            )}

            <FilterSection>
              <FilterTitle>Price Range</FilterTitle>
              <PriceRangeContainer>
                <PriceInput
                  type="number"
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={(e) =>
                    setPriceRange({ ...priceRange, min: e.target.value })
                  }
                  onBlur={handlePriceRangeChange}
                />
                <span>-</span>
                <PriceInput
                  type="number"
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={(e) =>
                    setPriceRange({ ...priceRange, max: e.target.value })
                  }
                  onBlur={handlePriceRangeChange}
                />
              </PriceRangeContainer>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Availability</FilterTitle>
              <FilterOption>
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) =>
                    handleFilterChange("inStock", e.target.checked)
                  }
                />
                <span>In Stock Only</span>
              </FilterOption>
              <FilterOption>
                <input
                  type="checkbox"
                  checked={onSaleOnly}
                  onChange={(e) =>
                    handleFilterChange("onSale", e.target.checked)
                  }
                />
                <span>On Sale</span>
              </FilterOption>
            </FilterSection>

            <ClearFiltersButton onClick={clearAllFilters}>
              Clear All Filters
            </ClearFiltersButton>
          </FilterSidebar>

          <MainContent>
            <ProductsHeader>
              <SortContainer>
                <SortLabel>Sort by:</SortLabel>
                <SortSelect value={sortBy} onChange={handleSortChange}>
                  <option value="date">Newest First</option>
                  <option value="popularity">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="price">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="title">Name: A to Z</option>
                </SortSelect>
              </SortContainer>

              <ViewToggle>
                <ViewButton
                  active={viewMode === "grid"}
                  onClick={() => setViewMode("grid")}
                >
                  Grid
                </ViewButton>
                <ViewButton
                  active={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                >
                  List
                </ViewButton>
              </ViewToggle>
            </ProductsHeader>

            <ResultsInfo>
              <ResultsCount>
                Showing {products.length} of {totalProducts} products
              </ResultsCount>
            </ResultsInfo>

            {loading ? (
              <LoadingSpinner fullHeight />
            ) : error ? (
              <div className="error">Failed to load products: {error}</div>
            ) : products.length > 0 ? (
              <ProductGrid viewMode={viewMode}>
                {products.map((product) => (
                  <ProductCard key={product.id} viewMode={viewMode}>
                    <ProductImage
                      image={productService.getProductImage(product)}
                      viewMode={viewMode}
                    >
                      {product.on_sale && <ProductBadge>Sale</ProductBadge>}
                    </ProductImage>

                    <ProductContent>
                      <ProductHeader>
                        {product.categories &&
                          product.categories.length > 0 && (
                            <ProductCategory>
                              {product.categories[0].name}
                            </ProductCategory>
                          )}
                        <ProductTitle>{product.name}</ProductTitle>

                        <ProductRating>
                          {renderStars(product.average_rating || 0)}
                          <RatingCount>
                            ({product.rating_count || 0} reviews)
                          </RatingCount>
                        </ProductRating>
                      </ProductHeader>

                      <ProductPrice>
                        <CurrentPrice>
                          {productService.formatPrice(product.price)}
                        </CurrentPrice>
                        {product.regular_price !== product.price && (
                          <OriginalPrice>
                            {productService.formatPrice(product.regular_price)}
                          </OriginalPrice>
                        )}
                      </ProductPrice>

                      <ProductDescription
                        viewMode={viewMode}
                        dangerouslySetInnerHTML={{
                          __html:
                            product.short_description || product.description,
                        }}
                      />

                      <ProductActions>
                        <Button
                          as={Link}
                          to={`/product/${product.id}`}
                          variant="outline"
                          size="sm"
                          style={{ flex: 1 }}
                        >
                          View Details
                        </Button>
                        <AddToCartButton
                          onClick={() => handleAddToCart(product)}
                          size="sm"
                          disabled={!productService.isProductInStock(product)}
                          className={addingStates[product.id] || ""}
                        >
                          <span className="button-text">
                            {addingStates[product.id] === "adding" ? (
                              <FaSpinner className="animate-spin" /> // spinner when adding
                            ) : addingStates[product.id] === "added" ? (
                              <FaCheck className="text-green-500" /> // checkmark when added
                            ) : (
                              <FaCartPlus /> // default cart icon
                            )}
                          </span>
                        </AddToCartButton>
                      </ProductActions>
                    </ProductContent>
                  </ProductCard>
                ))}
              </ProductGrid>
            ) : (
              <div>No products found</div>
            )}
          </MainContent>
        </ShopContent>

        {/* Animated View Cart Notification */}
        <ViewCartNotification show={showNotification}>
          <NotificationIcon>🛒</NotificationIcon>
          <NotificationContent>
            <NotificationTitle>Added to Cart!</NotificationTitle>
            <NotificationMessage>
              {addedProduct?.name} has been added to your cart
            </NotificationMessage>
          </NotificationContent>
          <ViewCartButton to="/cart">View Cart</ViewCartButton>
          <CloseNotificationButton onClick={closeNotification}>
            ×
          </CloseNotificationButton>
        </ViewCartNotification>
      </Container>
    </ShopWrapper>
  );
};

export default ShopPage;
